import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { session, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const checkSession = async () => {
      try {
        // Get fresh session
        const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error("Session error:", sessionError);
          await supabase.auth.signOut();
          toast.error("Session error. Please sign in again.");
          navigate("/login");
          return;
        }
        
        if (!currentSession) {
          console.log("No active session found");
          toast.error("Please sign in to access this page");
          navigate("/login");
          return;
        }

        // Add delay to ensure auth.users record exists
        await new Promise(resolve => setTimeout(resolve, 5000));

        // Ensure profile exists with retry logic
        let retryCount = 0;
        const maxRetries = 5;
        const retryDelay = 1000;
        
        while (retryCount < maxRetries) {
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', currentSession.user.id)
            .maybeSingle();

          if (profileError) {
            console.error("Profile fetch error:", profileError);
            retryCount++;
            if (retryCount === maxRetries) throw profileError;
            await new Promise(resolve => setTimeout(resolve, retryDelay * Math.pow(2, retryCount)));
            continue;
          }

          if (!profile) {
            try {
              console.log("Creating profile for user:", currentSession.user.id);
              const { error: createError } = await supabase
                .from('profiles')
                .insert([{ 
                  id: currentSession.user.id,
                  role: 'practitioner'
                }])
                .select()
                .maybeSingle();

              if (createError) {
                console.error("Profile creation error:", createError);
                retryCount++;
                if (retryCount === maxRetries) throw createError;
                await new Promise(resolve => setTimeout(resolve, retryDelay * Math.pow(2, retryCount)));
                continue;
              }
            } catch (insertError) {
              console.error("Profile insert error:", insertError);
              retryCount++;
              if (retryCount === maxRetries) throw insertError;
              await new Promise(resolve => setTimeout(resolve, retryDelay * Math.pow(2, retryCount)));
              continue;
            }
          }

          break;
        }

        // Skip email confirmation check if already on check-email page
        if (window.location.pathname === "/check-email") {
          return;
        }

        // Check email confirmation status
        if (!currentSession.user.email_confirmed_at) {
          console.log("Email not confirmed");
          toast.info("Please check your email to verify your account");
          navigate("/check-email");
          return;
        }

        // Check MFA status only if profile exists and has MFA enabled
        const { data: mfaProfile } = await supabase
          .from('profiles')
          .select('mfa_enabled, mfa_verified')
          .eq('id', currentSession.user.id)
          .maybeSingle();

        if (mfaProfile?.mfa_enabled && !mfaProfile?.mfa_verified) {
          // Skip redirect if already on verification page
          if (window.location.pathname !== "/verify-mfa") {
            console.log("MFA verification required");
            toast.info("Please complete two-factor authentication");
            navigate("/verify-mfa");
            return;
          }
        }
      } catch (error: any) {
        console.error("Session check error:", error);
        // Clear session on error and redirect to login
        await supabase.auth.signOut();
        toast.error("Authentication error. Please sign in again.");
        navigate("/login");
      }
    };

    if (!loading) {
      checkSession();
    }

    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        navigate("/login");
      }
    });

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, [loading, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return <>{children}</>;
};